/*!glb-autoplay-onthumb.js
 
  Scopo:
  Script che rileva interazioni (click, touch, focus, tastiera) sui thumbnail delle immagini di prodotto
  e tenta di avviare l'autoplay dei model-viewer 3D associati. Gestisce debounce/ de-duplication,
  osservatori di mutazione per thumbs dinamici, fallback globale quando non sono presenti model-viewer,
  e integrazione con UserActivationTrigger quando disponibile.
 
  Funzioni/esposizioni pubbliche:
  - window.__glb_onthumb_minimal_cleanup(): pulisce tutti i listener, observer e risorse allocate dallo script.
  - window.__glb_onthumb_diag(): ritorna uno stato diagnostico semplice (attached, pending, lastThumb, lastTarget, ...).
  - window.__glb_onthumb_minimal_handler_attached (boolean): flag se i handler sono stati collegati.
*/

(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  if (window.__glb_onthumb_minimal_executed) return;
  window.__glb_onthumb_minimal_executed = true;

  const win = window;
  const doc = (typeof document !== 'undefined') ? document : null;

  const CONFIG = {
    imgSelector: 'img.imgFt',
    keywordLo: '3dmodel',
    delayAfterClick: 120,
    quietScrollMs: 160,
    maxWaitMs: 1000,
    uatAutoplayWaitMs: 700,
    globalFallbackImgLimit: 50,
    productContainerSelectors: ['[data-product]', '#product', '.product-single', '.product__main']
  };

  let pending = null;
  let _lastTarget = null;
  let _lastThumb = null;
  let _globalFallbackDecision = null;
  const _scrollCleanupMap = (typeof WeakMap === 'function') ? new WeakMap() : null;
  let _mo = null;

  // cache esplicita: undefined = non valutato
  let _cachedFirstThumb = undefined;
  let _cachedProductContainer = undefined;

  win.__glb_onthumb_minimal_handler_attached = !!win.__glb_onthumb_minimal_handler_attached;

  // Inoltra errori a eventuale handler globale se definito
  function reportError(err) {
    try { if (typeof win.__glb_onthumb_onError === 'function') win.__glb_onthumb_onError(err); } catch (e) {}
  }

  // Determina se l'elemento passato e' un thumbnail valido (img.imgFt con src contenente keywordLo)
  function isThumbImg(el) {
    if (!el || el.nodeType !== 1) return false;
    try {
      if (el.tagName.toLowerCase() !== 'img') return false;
      const cl = el.classList;
      if (!cl || typeof cl.contains !== 'function' || !cl.contains('imgFt')) return false;
      const src = (el.currentSrc || el.src || '').toLowerCase();
      return src.indexOf(CONFIG.keywordLo) !== -1;
    } catch (e) { return false; }
  }

  // Decide se attivare un fallback globale basato sul numero di immagini nella pagina
  function assessGlobalFallback() {
    if (_globalFallbackDecision !== null) return _globalFallbackDecision;
    if (!doc) return (_globalFallbackDecision = false);
    try {
      if (win.__glb_single_model) return (_globalFallbackDecision = false);
      const imgs = doc.getElementsByTagName ? doc.getElementsByTagName('img') : null;
      _globalFallbackDecision = !!(imgs && imgs.length > 0 && imgs.length < CONFIG.globalFallbackImgLimit);
    } catch (e) { _globalFallbackDecision = false; }
    return _globalFallbackDecision;
  }

  // Invalida cache quando nodi vengono rimossi dal DOM
  function invalidateCacheIfDetached(node) {
    if (!node || !doc || node.nodeType !== 1) return;
    try {
      if (node === _lastThumb && !doc.contains(node)) { _lastTarget = null; _lastThumb = null; }
      if (_cachedFirstThumb && node === _cachedFirstThumb && !doc.contains(node)) { _cachedFirstThumb = null; }
      if (_cachedProductContainer && node === _cachedProductContainer && !doc.contains(node)) { _cachedProductContainer = null; }
    } catch (e) {}
  }

  // Restituisce il container principale del prodotto (cache)
  function getProductContainer() {
    if (_cachedProductContainer !== undefined) return _cachedProductContainer;
    if (!doc) return (_cachedProductContainer = null);
    try {
      for (let i = 0; i < CONFIG.productContainerSelectors.length; i++) {
        try {
          const el = doc.querySelector(CONFIG.productContainerSelectors[i]);
          if (el) return (_cachedProductContainer = el);
        } catch (e) {}
      }
    } catch (e) {}
    _cachedProductContainer = null;
    return _cachedProductContainer;
  }

  // Inizializza un MutationObserver generale per pulire cache se necessario (solo quando ha senso)
  (function initMutationObserverIfNeeded() {
    if (typeof MutationObserver !== 'function' || !doc) return;
    try {
      const mvs = doc.getElementsByTagName ? doc.getElementsByTagName('model-viewer') : [];
      const imgs = doc.getElementsByTagName ? doc.getElementsByTagName('img') : [];
      const mvCount = mvs && mvs.length ? mvs.length : 0;
      const imgCount = imgs && imgs.length ? imgs.length : 0;
      if ((win.__glb_single_model && mvCount <= 1) || (mvCount <= 1 && imgCount < CONFIG.globalFallbackImgLimit)) { _mo = null; return; }
      _mo = new MutationObserver(function (mutations) {
        for (let i = 0; i < mutations.length; i++) {
          const removed = mutations[i].removedNodes;
          if (!removed || removed.length === 0) continue;
          for (let j = 0; j < removed.length; j++) invalidateCacheIfDetached(removed[j]);
        }
      });
      _mo.observe(doc.documentElement || doc, { childList: true, subtree: true });
    } catch (e) { _mo = null; }
  })();

  // Recupera e memoizza il primo thumb valido trovato nel container prodotto
  function getCachedFirstThumb() {
    if (_cachedFirstThumb !== undefined) return _cachedFirstThumb;
    if (!doc || typeof doc.querySelector !== 'function') return (_cachedFirstThumb = null);
    try {
      const container = getProductContainer() || doc;
      const g = container.querySelector ? container.querySelector(CONFIG.imgSelector) : doc.querySelector(CONFIG.imgSelector);
      _cachedFirstThumb = (g && isThumbImg(g)) ? g : null;
    } catch (e) { _cachedFirstThumb = null; }
    return _cachedFirstThumb;
  }

  // Cerca il thumbnail più vicino partendo da target; usa caching semplice per evitare ricerche ripetute
  function findThumb(target) {
    if (!target || !doc) return null;
    if (target === _lastTarget) return _lastThumb;
    let n = target;
    while (n && n !== doc.body) {
      if (n.nodeType === 1 && isThumbImg(n)) { _lastTarget = target; _lastThumb = n; return n; }
      if (n.nodeType === 1 && n.querySelector) {
        try {
          const inside = n.querySelector(CONFIG.imgSelector);
          if (inside && isThumbImg(inside)) { _lastTarget = target; _lastThumb = inside; return inside; }
        } catch (e) {}
      }
      n = n.parentElement;
    }
    if (assessGlobalFallback()) {
      try {
        const g = getCachedFirstThumb();
        const result = g && isThumbImg(g) ? g : null;
        _lastTarget = target; _lastThumb = result; return result;
      } catch (e) {}
    }
    _lastTarget = target; _lastThumb = null; return null;
  }

  // Array di handler che provano ad avviare l'autoplay usando diverse integrazioni disponibili sulla pagina
  const autoplayHandlers = [
    function startModelViewer(wait) {
      if (typeof win.__glb_start_model_viewer === 'function') { win.__glb_start_model_viewer({ waitMs: wait }); return true; }
      return false;
    },
    function autostartScheduleRun(wait) {
      if (typeof win.__glb_autostart_scheduleRun === 'function') { win.__glb_autostart_scheduleRun(wait); return true; }
      return false;
    },
    function scheduleAutoplay(wait) {
      if (typeof win.__glb_scheduleAutoplay === 'function') return !!win.__glb_scheduleAutoplay(wait, false);
      return false;
    },
    function autoplayOnthumb(wait) {
      const s = win.__glb_autoplay_onthumb;
      return !!(s && typeof s.triggerNow === 'function' && s.triggerNow(wait));
    }
  ];

  // Chiama gli handler di autoplay in sequenza; ritorna true se uno ha avuto successo
  function callAutoplay(waitMs) {
    const wait = Number(waitMs) || CONFIG.uatAutoplayWaitMs;
    try {
      for (let i = 0; i < autoplayHandlers.length; i++) {
        if (autoplayHandlers[i](wait)) return true;
      }
    } catch (err) { reportError(err); }
    return false;
  }

  // Rilevamento se l'opzione passive negli addEventListener e' supportata
  let passiveOption = false;
  (function detectPassiveOption() {
    try {
      let supported = false;
      const opts = Object.defineProperty({}, 'passive', {
        get: function () { supported = true; return true; }
      });
      if (doc && doc.addEventListener) { doc.addEventListener('testPassive', null, opts); doc.removeEventListener('testPassive', null, opts); }
      passiveOption = supported ? { passive: true } : false;
    } catch (e) { passiveOption = false; }
  })();

  // Aggiunge listener 'scroll' e attende che lo scrolling sia quieto oppure timeout globale
  function waitForScrollToSettle(container, onSettled, maxWait) {
    container = container || doc || null;
    maxWait = (typeof maxWait === 'number') ? maxWait : CONFIG.maxWaitMs;
    let quietTimer = null;
    let overallTimer = null;
    function handler() {
      if (quietTimer) clearTimeout(quietTimer);
      quietTimer = setTimeout(finishByQuiet, CONFIG.quietScrollMs);
    }
    function cleanup() {
      try { container && container.removeEventListener('scroll', handler, passiveOption); } catch (e) {}
      if (quietTimer) { clearTimeout(quietTimer); quietTimer = null; }
      if (overallTimer) { clearTimeout(overallTimer); overallTimer = null; }
      try { if (_scrollCleanupMap && _scrollCleanupMap.get(container) === cleanup) _scrollCleanupMap.delete(container); } catch (e) {}
    }
    function finish(byQuiet) { cleanup(); try { onSettled(byQuiet); } catch (e) { reportError(e); } }
    function finishByQuiet() { finish(true); }

    try { container && container.addEventListener('scroll', handler, passiveOption); } catch (e) {}
    try { if (_scrollCleanupMap && !_scrollCleanupMap.has(container)) _scrollCleanupMap.set(container, cleanup); } catch (e) {}
    quietTimer = setTimeout(function () { finish(false); }, CONFIG.quietScrollMs + 20);
    overallTimer = setTimeout(function () { finish(false); }, maxWait);
    return cleanup;
  }

  // Verifica tramite UserActivationTrigger se un elemento e' gia' stato attivato dall'utente
  function isModelViewerAlreadyActivatedForElement(el) {
    try {
      const UAT = win.UserActivationTrigger || null;
      if (!UAT || !UAT._internal || typeof UAT._internal.findRecordForElement !== 'function') return false;
      const rec = UAT._internal.findRecordForElement(el, { force: false });
      return !!(rec && rec.triggered);
    } catch (e) { return false; }
  }

  // Trova il model-viewer piu' vicino al container (cerca in shadowRoot, nel container, nel parent)
  async function findNearestModelViewerForContainer(container) {
    try {
      const UAT = win.UserActivationTrigger || null;
      if (UAT && typeof UAT.waitForModelInsertionNear === 'function') {
        const mv = await UAT.waitForModelInsertionNear(container, 600).catch(() => null);
        if (mv) return mv;
      }
      if (!container) return doc ? doc.querySelector('model-viewer') : null;
      try {
        if (container.shadowRoot && container.shadowRoot.querySelector) {
          const inside = container.shadowRoot.querySelector('model-viewer'); if (inside) return inside;
        }
        if (container.querySelector) {
          const inside2 = container.querySelector('model-viewer'); if (inside2) return inside2;
        }
        const parent = (container.closest && container.closest('.product__media, .media, .product-single')) || container.parentNode;
        if (parent && parent.querySelector) {
          const pfound = parent.querySelector('model-viewer'); if (pfound) return pfound;
        }
      } catch (e) {}
      return null;
    } catch (e) { return null; }
  }

  // Prova ad avviare autoplay una sola volta per un dato container, usando UAT quando possibile
  async function tryStartAutoplayOnceForContainer(container) {
    try {
      if (isModelViewerAlreadyActivatedForElement(container)) return false;
      const mv = await findNearestModelViewerForContainer(container);
      if (mv) {
        if (isModelViewerAlreadyActivatedForElement(mv)) return false;
        const UAT = win.UserActivationTrigger || null;
        if (UAT && typeof UAT.notifyElementActivated === 'function') {
          try {
            const notified = UAT.notifyElementActivated(mv, 'thumb:autoplay-request', { requestedBy: 'autoplay-onthumb' }, { force: true });
            if (notified) return true;
          } catch (e) {}
        }
      }
      return callAutoplay(CONFIG.uatAutoplayWaitMs);
    } catch (err) { reportError(err); return false; }
  }

  // Programma l'avvio dell'autoplay dopo che il thumb si e' stabilizzato (delay + waitForScrollToSettle)
  function scheduleAutoplayAfterThumbSettles(container) {
    if (pending && typeof pending.cleanup === 'function') {
      try { pending.cleanup(); } catch (e) { reportError(e); }
      pending = null;
    }
    const startTs = Date.now();
    const timeoutId = setTimeout(function () {
      const remaining = Math.max(0, CONFIG.maxWaitMs - (Date.now() - startTs));
      const cleanupScroll = waitForScrollToSettle(container || doc, async function () {
        pending = null;
        try { await tryStartAutoplayOnceForContainer(container); } catch (e) { reportError(e); }
      }, remaining);
      pending = { cleanup: cleanupScroll };
    }, CONFIG.delayAfterClick);
    pending = { cleanup: function () { clearTimeout(timeoutId); } };
  }

  const FOCUS_TRIGGER_DELAY = 40;
  const _thumbObservers = (typeof WeakMap === 'function') ? new WeakMap() : new Map();
  const _resourceRegistry = (typeof Map === 'function') ? new Map() : Object.create(null);

  function registrySet(key, value) { try { if (typeof _resourceRegistry.set === 'function') return _resourceRegistry.set(key, value); _resourceRegistry[key] = value; } catch (e) {} }
  function registryGet(key) { try { return (typeof _resourceRegistry.get === 'function') ? _resourceRegistry.get(key) : _resourceRegistry[key]; } catch (e) { return undefined; } }
  function registryDelete(key) { try { if (typeof _resourceRegistry.delete === 'function') return _resourceRegistry.delete(key); delete _resourceRegistry[key]; } catch (e) {} }

  // Chiamata quando un thumb e' considerato "attivato" (focus, click, ecc.)
  function onThumbConsideredActivated(thumb) {
    if (!thumb) return;
    const productContainer = getProductContainer() || doc;
    const container = thumb.parentElement || productContainer;
    setTimeout(function () { scheduleAutoplayAfterThumbSettles(container); }, FOCUS_TRIGGER_DELAY);
  }

  // Osserva mutate attributes su un singolo thumb per intercettare cambi di classe/aria che indicano attivazione
  function observeThumbMutations(thumb) {
    if (!thumb) return;
    try { if (_thumbObservers.get && _thumbObservers.get(thumb)) return; } catch (e) {}
    let mo = null;
    try {
      mo = new MutationObserver(function (mrs) {
        for (let i = 0; i < mrs.length; i++) {
          const mr = mrs[i];
          if (mr.type === 'attributes') {
            const attr = mr.attributeName || '';
            if (attr === 'class' || /selected|active|aria-pressed|aria-selected|aria-current/i.test(attr)) {
              const cls = thumb.className || '';
              const activeLikely = /\b(active|is-active|selected|current)\b/.test(cls) || !!(thumb.getAttribute && thumb.getAttribute('aria-selected'));
              if (activeLikely) { onThumbConsideredActivated(thumb); return; }
            }
          }
        }
      });
      mo.observe(thumb, { attributes: true, attributeFilter: ['class','aria-selected','aria-pressed','aria-current'] });
    } catch (e) { mo = null; }
    try { _thumbObservers.set(thumb, mo || true); } catch (e) {}
  }

  // Ferma l'osservazione di un thumb
  function unobserveThumb(thumb) {
    try {
      if (!_thumbObservers) return;
      const o = _thumbObservers.get ? _thumbObservers.get(thumb) : _thumbObservers.get(thumb);
      if (o && typeof o.disconnect === 'function') try { o.disconnect(); } catch (e) {}
      try { _thumbObservers.delete(thumb); } catch (e) {}
    } catch (e) {}
  }

  // Handler focusin: se il target e' un thumb, pianifica autoplay
  function onDocumentFocusIn(ev) {
    try {
      const thumb = findThumb(ev && ev.target);
      if (!thumb) return;
      onThumbConsideredActivated(thumb);
    } catch (e) { reportError(e); }
  }

  // Handler keydown: Enter/Space attivano come click
  function onDocumentKeyDown(ev) {
    try {
      if (!ev) return;
      const k = ev.key;
      if (k !== 'Enter' && k !== ' ' && k !== 'Spacebar') return;
      const thumb = findThumb(ev.target);
      if (!thumb) return;
      onThumbConsideredActivated(thumb);
    } catch (e) { reportError(e); }
  }

  // Scansiona root per thumbs esistenti e li osserva
  function scanAndObserveThumbs(root) {
    try {
      root = root || doc;
      const list = root.querySelectorAll ? root.querySelectorAll(CONFIG.imgSelector) : [];
      for (let i = 0, l = list.length; i < l; i++) {
        const t = list[i];
        if (!t) continue;
        if (!isThumbImg(t)) continue;
        observeThumbMutations(t);
      }
    } catch (e) {}
  }

  // Collega listener per focus/keydown e inizializza mutation observer locale per nodi aggiunti/rimossi
  function attachHandlersByFocus() {
    if (!doc) return;
    if (win.__glb_onthumb_minimal_handler_attached) return;
    try {
      document.addEventListener('focusin', onDocumentFocusIn, true);
      document.addEventListener('keydown', onDocumentKeyDown, true);

      if (typeof MutationObserver === 'function') {
        try {
          const root = doc.documentElement || doc.body || doc;
          const localMo = new MutationObserver(function (mrs) {
            for (let i = 0; i < mrs.length; i++) {
              const mr = mrs[i];
              const added = mr.addedNodes || [];
              for (let j = 0; j < added.length; j++) {
                const n = added[j];
                try {
                  if (!n || n.nodeType !== 1) continue;
                  if (n.matches && n.matches(CONFIG.imgSelector) && isThumbImg(n)) observeThumbMutations(n);
                  else if (n.querySelector) {
                    const list = n.querySelectorAll ? n.querySelectorAll(CONFIG.imgSelector) : [];
                    for (let k = 0, lk = list.length; k < lk; k++) {
                      const t = list[k];
                      if (isThumbImg(t)) observeThumbMutations(t);
                    }
                  }
                } catch (e) {}
              }
              const removed = mr.removedNodes || [];
              for (let j = 0; j < removed.length; j++) {
                const rn = removed[j];
                try {
                  if (!rn || rn.nodeType !== 1) continue;
                  if (rn.matches && rn.matches(CONFIG.imgSelector)) unobserveThumb(rn);
                  else if (rn.querySelector) {
                    const list = rn.querySelectorAll ? rn.querySelectorAll(CONFIG.imgSelector) : [];
                    for (let k = 0, lk = list.length; k < lk; k++) unobserveThumb(list[k]);
                  }
                } catch (e) {}
              }
            }
          });
          try { localMo.observe(root, { childList: true, subtree: true }); } catch (e) {}
          try { registrySet('localMo', localMo); } catch (e) {}
        } catch (e) {}
      }

      scanAndObserveThumbs(doc);

      win.__glb_onthumb_minimal_handler_attached = true;
      win.__glb_onthumb_minimal_handler = onDocumentFocusIn;
      win.__glb_onthumb_minimal_attached_target = document;
    } catch (e) { reportError(e); }
  }

  // Rimuove listener di focus/keydown e disconnecta observer dei thumbs
  function cleanupFocusObservers() {
    try { document.removeEventListener('focusin', onDocumentFocusIn, true); } catch (e) {}
    try { document.removeEventListener('keydown', onDocumentKeyDown, true); } catch (e) {}
    try {
      if (_thumbObservers) {
        if (typeof _thumbObservers.forEach === 'function') {
          _thumbObservers.forEach((val) => { try { if (val && typeof val.disconnect === 'function') val.disconnect(); } catch (e) {} });
          try { _thumbObservers.clear(); } catch (e) {}
        } else {
          try {
            const entries = Array.from(_thumbObservers.keys ? _thumbObservers.keys() : []);
            for (let i = 0; i < entries.length; i++) {
              const k = entries[i];
              const v = _thumbObservers.get(k);
              try { if (v && typeof v.disconnect === 'function') v.disconnect(); } catch (e) {}
            }
          } catch (e) {}
        }
      }
      const localMo = registryGet('localMo');
      if (localMo && typeof localMo.disconnect === 'function') try { localMo.disconnect(); } catch (e) {}
      try { registryDelete('localMo'); } catch (e) {}
    } catch (e) {}
  }

  const TOUCH_MOVE_THRESHOLD = 10;
  const _touchStartMap = (typeof WeakMap === 'function') ? new WeakMap() : new Map();

  // Touchstart: memorizza posizione iniziale per poi valutare se c'è stato swipe/move
  function onDocumentTouchStartForThumb(ev) {
    try {
      if (!ev || !ev.touches || ev.touches.length === 0) return;
      const t = ev.touches[0];
      // proviamo prima a risalire dal target, poi fallback a elementFromPoint
      let thumb = findThumb(t.target || ev.target);
      if (!thumb && doc && typeof doc.elementFromPoint === 'function') {
        const el = doc.elementFromPoint(t.clientX, t.clientY);
        thumb = findThumb(el);
      }
      if (!thumb) {
        // memorizziamo comunque la posizione globale per possibili rilevamenti successivi
        try { _touchStartMap.set && _touchStartMap.set(doc, { x: t.clientX, y: t.clientY }); } catch (e) {}
        return;
      }
      try { _touchStartMap.set && _touchStartMap.set(thumb, { x: t.clientX, y: t.clientY }); } catch (e) {}
    } catch (e) { reportError(e); }
  }

  // Touchmove: se il movimento supera soglia, considera attivazione e pianifica autoplay
  function onDocumentTouchMoveForThumb(ev) {
    try {
      if (!ev || !ev.touches || ev.touches.length === 0) return;
      const t = ev.touches[0];
      // cerchiamo il thumb sotto il dito con elementFromPoint (più affidabile su carousel con overlays)
      let thumb = findThumb(t.target || ev.target);
      if ((!thumb || !( _touchStartMap.get && _touchStartMap.get(thumb) )) && doc && typeof doc.elementFromPoint === 'function') {
        const el = doc.elementFromPoint(t.clientX, t.clientY);
        thumb = findThumb(el) || thumb;
      }
      // se non troviamo un thumb specifico, prova fallback globale (controlliamo stato memorizzato su doc)
      const start = thumb ? (_touchStartMap.get ? _touchStartMap.get(thumb) : null) : (_touchStartMap.get ? _touchStartMap.get(doc) : null);
      if (!start) return;
      const dx = Math.abs((t.clientX || 0) - (start.x || 0));
      const dy = Math.abs((t.clientY || 0) - (start.y || 0));
      if (dx >= TOUCH_MOVE_THRESHOLD || dy >= TOUCH_MOVE_THRESHOLD) {
        const productContainer = getProductContainer() || doc;
        const container = (thumb && thumb.parentElement) || productContainer;
        scheduleAutoplayAfterThumbSettles(container);
        try {
          if (thumb) _touchStartMap.delete && _touchStartMap.delete(thumb);
          else _touchStartMap.delete && _touchStartMap.delete(doc);
        } catch (e) {}
      }
    } catch (e) { reportError(e); }
  }

  // Touchend: pulisce stato touch per il thumb e prova ad attivare se il dito finisce sopra un thumb
  function onDocumentTouchEndForThumb(ev) {
    try {
      const t = ev && ev.changedTouches && ev.changedTouches[0] ? ev.changedTouches[0] : null;
      // prima prova a usare elementFromPoint alla posizione finale
      let thumb = null;
      if (t && doc && typeof doc.elementFromPoint === 'function') {
        const el = doc.elementFromPoint(t.clientX, t.clientY);
        thumb = findThumb(el);
      }
      // fallback al target o al changed target
      if (!thumb) {
        const tgt = ev && ev.changedTouches && ev.changedTouches[0] ? ev.changedTouches[0].target : ev && ev.target;
        thumb = findThumb(tgt);
      }
      if (thumb) {
        try { _touchStartMap.delete && _touchStartMap.delete(thumb); } catch (e) {}
        // consideriamo il thumb "attivato" al termine del tocco
        onThumbConsideredActivated(thumb);
        return;
      }
      // pulizia generale fallback
      try { _touchStartMap.delete && _touchStartMap.delete(doc); } catch (e) {}
    } catch (e) { reportError(e); }
  }

  // Collega i gestori touch per mobile (start/move/end/cancel)
  function attachMobileTouchHandlers() {
    try {
      document.addEventListener('touchstart', onDocumentTouchStartForThumb, { passive: true, capture: true });
      document.addEventListener('touchmove', onDocumentTouchMoveForThumb, { passive: true, capture: true });
      document.addEventListener('touchend', onDocumentTouchEndForThumb, { passive: true, capture: true });
      document.addEventListener('touchcancel', onDocumentTouchEndForThumb, { passive: true, capture: true });
      try { registrySet('touchHandlers', true); } catch (e) {}
    } catch (e) { reportError(e); }
  }

  // Rimuove i gestori touch mobile
  function detachMobileTouchHandlers() {
    try {
      document.removeEventListener('touchstart', onDocumentTouchStartForThumb, { capture: true });
      document.removeEventListener('touchmove', onDocumentTouchMoveForThumb, { capture: true });
      document.removeEventListener('touchend', onDocumentTouchEndForThumb, { capture: true });
      document.removeEventListener('touchcancel', onDocumentTouchEndForThumb, { capture: true });
      try { registryDelete('touchHandlers'); } catch (e) {}
    } catch (e) { reportError(e); }
  }

  // Delegazione eventi e observer del container prodotto per nodi dinamici; de-dup tramite WeakSet/Map
  (function delegateAndContainerObserver() {
    try {
      if (!doc) return;
      const __glb_onthumb_recent = (typeof WeakSet === 'function') ? new WeakSet() : new Map();
      const DEDUP_TTL = 800;

      function markRecent(el) {
        try {
          if (!el) return;
          if (__glb_onthumb_recent.add) {
            __glb_onthumb_recent.add(el);
            setTimeout(() => { try { __glb_onthumb_recent.delete(el); } catch (e) {} }, DEDUP_TTL);
          } else {
            __glb_onthumb_recent.set(el, Date.now());
            setTimeout(() => { try { __glb_onthumb_recent.delete(el); } catch (e) {} }, DEDUP_TTL);
          }
        } catch (e) {}
      }
      function isRecent(el) {
        try {
          if (!el) return false;
          if (__glb_onthumb_recent.has) return __glb_onthumb_recent.has(el);
          return __glb_onthumb_recent.get && (__glb_onthumb_recent.get(el) !== undefined);
        } catch (e) { return false; }
      }

      const productContainer = getProductContainer() || doc.body;

      // Handler delegato per pointer/click che individua il thumb target e avvia flusso
      function onDelegatedPointer(ev) {
        try {
          const tgt = ev && ev.target;
          const thumb = findThumb(tgt);
          if (!thumb) return;
          if (isRecent(thumb)) return;
          markRecent(thumb);
          onThumbConsideredActivated(thumb);
        } catch (e) { reportError(e); }
      }

      try {
        if (window.PointerEvent) {
          productContainer.addEventListener('pointerdown', onDelegatedPointer, true);
          productContainer.addEventListener('pointerup', onDelegatedPointer, true);
        } else {
          productContainer.addEventListener('click', onDelegatedPointer, true);
        }
        try { registrySet('delegatedPointer', true); } catch (e) {}
      } catch (e) { reportError(e); }

      try {
        if (typeof MutationObserver === 'function') {
          const containerMo = new MutationObserver(function (mrs) {
            for (let i = 0; i < mrs.length; i++) {
              const mr = mrs[i];
              const added = mr.addedNodes || [];
              for (let j = 0; j < added.length; j++) {
                const n = added[j];
                try {
                  if (!n || n.nodeType !== 1) continue;
                  if (n.matches && n.matches(CONFIG.imgSelector) && isThumbImg(n)) observeThumbMutations(n);
                  else if (n.querySelector) {
                    const list = n.querySelectorAll ? n.querySelectorAll(CONFIG.imgSelector) : [];
                    for (let k = 0, lk = list.length; k < lk; k++) {
                      const t = list[k];
                      if (isThumbImg(t)) observeThumbMutations(t);
                    }
                  }
                } catch (e) {}
              }
              const removed = mr.removedNodes || [];
              for (let j = 0; j < removed.length; j++) {
                const rn = removed[j];
                try {
                  if (!rn || rn.nodeType !== 1) continue;
                  if (rn.matches && rn.matches(CONFIG.imgSelector)) unobserveThumb(rn);
                  else if (rn.querySelector) {
                    const list = rn.querySelectorAll ? rn.querySelectorAll(CONFIG.imgSelector) : [];
                    for (let k = 0, lk = list.length; k < lk; k++) unobserveThumb(list[k]);
                  }
                } catch (e) {}
              }
            }
          });
          try { containerMo.observe(productContainer || doc.body, { childList: true, subtree: true }); } catch (e) {}
          try { registrySet('containerMo', containerMo); } catch (e) {}
        }
      } catch (e) { reportError(e); }

      const prevCleanup = win.__glb_onthumb_minimal_cleanup;
      // Espone cleanup che fa uso di eventuale cleanup precedente
      win.__glb_onthumb_minimal_cleanup = function () {
        try { if (prevCleanup) prevCleanup(); } catch (e) {}
        try {
          if (productContainer) {
            if (window.PointerEvent) {
              productContainer.removeEventListener('pointerdown', onDelegatedPointer, true);
              productContainer.removeEventListener('pointerup', onDelegatedPointer, true);
            } else {
              productContainer.removeEventListener('click', onDelegatedPointer, true);
            }
          }
          const cm = registryGet('containerMo');
          if (cm && typeof cm.disconnect === 'function') try { cm.disconnect(); } catch (e) {}
          try { registryDelete('containerMo'); } catch (e) {}
          try { registryDelete('delegatedPointer'); } catch (e) {}
        } catch (e) {}
      };
    } catch (e) { reportError(e); }
  })();

  // Pulisce tutte le risorse allocate dallo script: listener, observer, pending timers, cache
  function cleanupAll() {
    if (!doc) return;
    try { cleanupFocusObservers(); } catch (e) {}
    try { detachMobileTouchHandlers(); } catch (e) {}
    try { if (pending && pending.cleanup) pending.cleanup(); } catch (e) { reportError(e); }
    try { if (_mo && typeof _mo.disconnect === 'function') { _mo.disconnect(); _mo = null; } } catch (e) {}
    try { delete win.__glb_onthumb_minimal_handler; } catch (e) {}
    try { delete win.__glb_onthumb_minimal_handler_attached; } catch (e) {}
    try { delete win.__glb_onthumb_minimal_cleanup; } catch (e) {}
    try { delete win.__glb_onthumb_minimal_attached_target; } catch (e) {}
    _cachedFirstThumb = undefined;
    _cachedProductContainer = undefined;
  }

  // Espone la funzione di cleanup principale
  win.__glb_onthumb_minimal_cleanup = cleanupAll;

  // Espone diagnostica minimale per debugging esterno
  win.__glb_onthumb_diag = function () {
    return {
      attached: !!win.__glb_onthumb_minimal_handler_attached,
      pending: !!pending,
      lastThumb: _lastThumb || null,
      lastTarget: _lastTarget || null,
      cachedFirstThumb: (_cachedFirstThumb === undefined ? 'not-evaluated' : (_cachedFirstThumb ? 'present' : 'null')),
      attachedTarget: (win.__glb_onthumb_minimal_attached_target ? (win.__glb_onthumb_minimal_attached_target.id || win.__glb_onthumb_minimal_attached_target.tagName || 'container') : null)
    };
  };

  // Inizializzazione condizionale: tenta di sincronizzarsi con UserActivationTrigger se presente, altrimenti collega handlers subito
  (function conditionalInit() {
    if (win.__glb_onthumb_minimal_handler_attached) return;
    if (!doc || typeof doc.querySelector !== 'function') { attachHandlersByFocus(); attachMobileTouchHandlers(); return; }
    getCachedFirstThumb();
    getProductContainer();
    try {
      const UAT = win.UserActivationTrigger || null;
      if (UAT && typeof UAT.waitForModelReady === 'function') {
        const mv = doc.querySelector('model-viewer');
        if (mv) {
          UAT.waitForModelReady(mv, { timeout: 250, interval: 80 }).then(function (ready) {
            if (!ready && !win.__glb_onthumb_minimal_handler_attached) { attachHandlersByFocus(); attachMobileTouchHandlers(); }
          }).catch(function () {
            if (!win.__glb_onthumb_minimal_handler_attached) { attachHandlersByFocus(); attachMobileTouchHandlers(); }
          });
          return;
        }
      }
    } catch (e) { reportError(e); }
    attachHandlersByFocus();
    attachMobileTouchHandlers();
  })();

})();
